import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, MapPin, Building, FileText, ExternalLink, Clock, Eye, User } from 'lucide-react';
import apiClient from '../lib/api';
import './SharedAlert.css';

const SharedAlert: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [alert, setAlert] = useState<any>(null);
  const [sharedInfo, setSharedInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedAlert = async () => {
      if (!token) {
        setError('No share token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.alerts.getShared(token);
        setAlert(response.alert);
        setSharedInfo(response.shared_info);
      } catch (error: any) {
        console.error('Error fetching shared alert:', error);
        if (error.response?.status === 404) {
          setError('This shared alert could not be found. The link may be invalid or expired.');
        } else if (error.response?.status === 410) {
          setError('This shared link has expired.');
        } else {
          setError(error.response?.data?.detail || 'Failed to load shared alert');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedAlert();
  }, [token]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'urgent':
        return '#ef4444';
      case 'high':
        return '#f59e0b';
      case 'normal':
        return '#BADA55';
      case 'low':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const getSeverityBadge = (severity: string) => {
    const color = getSeverityColor(severity);
    return (
      <span 
        className="severity-badge"
        style={{ 
          backgroundColor: `${color}20`,
          color: color,
          border: `1px solid ${color}50`
        }}
      >
        {severity?.toUpperCase() || 'NORMAL'}
      </span>
    );
  };

  const formatMeetingType = (meetingType: string) => {
    if (!meetingType) return 'Not specified';
    // Replace underscores with spaces and capitalize each word
    return meetingType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <div className="shared-alert-page">
        <div className="shared-alert-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading shared alert...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="shared-alert-page">
        <div className="shared-alert-container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Alert Not Available</h2>
            <p>{error || 'This alert could not be loaded.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-alert-page">
      <div className="shared-alert-container">
        {/* Header with branding */}
        <div className="shared-alert-header">
          <div className="cityscrape-logo">
            <Building size={24} />
            <span>CityScrape</span>
          </div>
          <div className="shared-badge">
            Shared Alert
          </div>
        </div>

        {/* Share info banner */}
        {sharedInfo && (
          <div className="share-info-banner">
            <div className="share-info-item">
              <User size={16} />
              <span>Shared by {sharedInfo.shared_by}</span>
            </div>
            <div className="share-info-item">
              <Clock size={16} />
              <span>Shared {formatDate(sharedInfo.created_at)}</span>
            </div>
            <div className="share-info-item">
              <Eye size={16} />
              <span>{sharedInfo.view_count} views</span>
            </div>
            {sharedInfo.expires_at && (
              <div className="share-info-item warning">
                <Clock size={16} />
                <span>Expires {formatDate(sharedInfo.expires_at)}</span>
              </div>
            )}
          </div>
        )}

        {/* Main alert content */}
        <div className="alert-main-content">
          {/* Title and severity */}
          <div className="alert-title-section">
            <div className="alert-title-row">
              <h1>{alert.meeting_name || 'Municipal Alert'}</h1>
              {getSeverityBadge(alert.severity)}
            </div>
          </div>

          {/* Metadata */}
          <div className="alert-metadata">
            <div className="metadata-item">
              <MapPin size={18} />
              <div>
                <div className="metadata-label">Municipality</div>
                <div className="metadata-value">{alert.municipality || 'Not specified'}</div>
              </div>
            </div>

            <div className="metadata-item">
              <Calendar size={18} />
              <div>
                <div className="metadata-label">Meeting Date</div>
                <div className="metadata-value">{formatDate(alert.meeting_date)}</div>
              </div>
            </div>

            {alert.meeting_type && (
              <div className="metadata-item">
                <FileText size={18} />
                <div>
                  <div className="metadata-label">Meeting Type</div>
                  <div className="metadata-value">{formatMeetingType(alert.meeting_type)}</div>
                </div>
              </div>
            )}

            {alert.property_name && (
              <div className="metadata-item">
                <Building size={18} />
                <div>
                  <div className="metadata-label">Property</div>
                  <div className="metadata-value">
                    {alert.property_name}
                    {alert.property_address && (
                      <span className="property-address"> • {alert.property_address}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Alert Summary */}
          {alert.alert_summary && (
            <div className="alert-section">
              <h2>Alert Summary</h2>
              <div className="alert-summary-content">
                {alert.alert_summary}
              </div>
            </div>
          )}

          {/* Meeting Summary */}
          {alert.meeting_summary && (
            <div className="alert-section">
              <h2>Meeting Summary</h2>
              <div className="meeting-summary-content">
                {alert.meeting_summary}
              </div>
            </div>
          )}

          {/* Property Impacts */}
          {alert.property_impacts && alert.property_impacts.length > 0 && (
            <div className="alert-section">
              <h2>Property Impacts ({alert.property_impacts.length})</h2>
              <div className="property-impacts-list">
                {alert.property_impacts.map((impact: any, index: number) => (
                  <div key={index} className="property-impact-item">
                    <div className="impact-header">
                      <span className="impact-number">Item {impact.item_number || index + 1}</span>
                      {impact.confidence && (
                        <span className="impact-confidence">{impact.confidence}</span>
                      )}
                    </div>
                    <h3 className="impact-title">{impact.title}</h3>
                    {impact.impact_summary && (
                      <p className="impact-summary">{impact.impact_summary}</p>
                    )}
                    {impact.expanded_finding && (
                      <div className="impact-finding">
                        <strong>Finding:</strong> {impact.expanded_finding}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Source document link */}
          {alert.document_url && (
            <div className="alert-section">
              <a 
                href={alert.document_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="source-document-link"
              >
                <FileText size={20} />
                <span>View Source Document</span>
                <ExternalLink size={16} />
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shared-alert-footer">
          <p>Powered by <strong>CityScrape</strong> • Municipal Intelligence Platform</p>
          <p className="footer-note">This is a read-only view of a shared alert</p>
        </div>
      </div>
    </div>
  );
};

export default SharedAlert;

